import type { DatabaseEngineId } from '@/core/engines';
import type { SqlSnippetTemplate } from './sqlSnippetTemplateTypes';
import { byEngine } from './sqlSnippetTemplateUtils';

export function getBuiltInSqlSnippetTemplatesDdlAlterDrop(engineId: DatabaseEngineId): readonly SqlSnippetTemplate[] {
  if (engineId === 'oracle') {
    return [
      {
        id: 'alter-table-add-column',
        label: 'ALTER TABLE … ADD …',
        detail: 'Built-in snippet',
        documentation: 'Add a column to a table (Oracle)',
        filterText: 'alter table add column',
        insertText: 'ALTER TABLE ${1:schema}.${2:table}\nADD ${3:column} ${4:VARCHAR2(255)};',
      },
      {
        id: 'alter-table-drop-column',
        label: 'ALTER TABLE … DROP COLUMN …',
        detail: 'Built-in snippet',
        documentation: 'Drop a column from a table',
        filterText: 'alter table drop column',
        insertText: 'ALTER TABLE ${1:schema}.${2:table}\nDROP COLUMN ${3:column};',
      },
      {
        id: 'alter-table-modify-column',
        label: 'ALTER TABLE … MODIFY …',
        detail: 'Built-in snippet',
        documentation: 'Modify a column type (Oracle)',
        filterText: 'alter table modify column type',
        insertText: 'ALTER TABLE ${1:schema}.${2:table}\nMODIFY ${3:column} ${4:VARCHAR2(255)} NOT NULL;',
      },
      {
        id: 'alter-table-add-foreign-key',
        label: 'ALTER TABLE … ADD CONSTRAINT … FOREIGN KEY …',
        detail: 'Built-in snippet',
        documentation: 'Add a foreign key constraint',
        filterText: 'alter table add constraint foreign key references',
        insertText:
          'ALTER TABLE ${1:schema}.${2:table}\nADD CONSTRAINT ${3:fk_name}\nFOREIGN KEY (${4:column})\nREFERENCES ${5:schema}.${6:ref_table}(${7:ref_column});',
      },
      {
        id: 'alter-table-add-unique',
        label: 'ALTER TABLE … ADD CONSTRAINT … UNIQUE …',
        detail: 'Built-in snippet',
        documentation: 'Add a unique constraint',
        filterText: 'alter table add constraint unique',
        insertText:
          'ALTER TABLE ${1:schema}.${2:table}\nADD CONSTRAINT ${3:uq_name}\nUNIQUE (${4:column});',
      },
      {
        id: 'alter-sequence',
        label: 'ALTER SEQUENCE …',
        detail: 'Built-in snippet',
        documentation: 'Alter sequence properties (Oracle)',
        filterText: 'alter sequence increment cache',
        insertText: 'ALTER SEQUENCE ${1:schema}.${2:seq_name}\n  INCREMENT BY ${3:1}\n  CACHE ${4:20};',
      },
      {
        id: 'drop-table',
        label: 'DROP TABLE … CASCADE CONSTRAINTS',
        detail: 'Built-in snippet',
        documentation: 'Drop table with CASCADE CONSTRAINTS (Oracle)',
        filterText: 'drop table cascade constraints purge',
        insertText: 'DROP TABLE ${1:schema}.${2:table} CASCADE CONSTRAINTS PURGE;',
      },
      {
        id: 'drop-view',
        label: 'DROP VIEW …',
        detail: 'Built-in snippet',
        documentation: 'Drop view (Oracle)',
        filterText: 'drop view',
        insertText: 'DROP VIEW ${1:schema}.${2:view_name};',
      },
      {
        id: 'drop-index',
        label: 'DROP INDEX …',
        detail: 'Built-in snippet',
        documentation: 'Drop index (Oracle)',
        filterText: 'drop index',
        insertText: 'DROP INDEX ${1:schema}.${2:index_name};',
      },
      {
        id: 'drop-sequence',
        label: 'DROP SEQUENCE …',
        detail: 'Built-in snippet',
        documentation: 'Drop sequence (Oracle)',
        filterText: 'drop sequence',
        insertText: 'DROP SEQUENCE ${1:schema}.${2:seq_name};',
      },
      {
        id: 'drop-procedure',
        label: 'DROP PROCEDURE …',
        detail: 'Built-in snippet',
        documentation: 'Drop procedure (Oracle)',
        filterText: 'drop procedure',
        insertText: 'DROP PROCEDURE ${1:schema}.${2:proc_name};',
      },
      {
        id: 'drop-function',
        label: 'DROP FUNCTION …',
        detail: 'Built-in snippet',
        documentation: 'Drop function (Oracle)',
        filterText: 'drop function',
        insertText: 'DROP FUNCTION ${1:schema}.${2:func_name};',
      },
      {
        id: 'drop-package',
        label: 'DROP PACKAGE …',
        detail: 'Built-in snippet',
        documentation: 'Drop package (Oracle)',
        filterText: 'drop package',
        insertText: 'DROP PACKAGE ${1:schema}.${2:pkg_name};',
      },
      {
        id: 'drop-synonym',
        label: 'DROP SYNONYM …',
        detail: 'Built-in snippet',
        documentation: 'Drop synonym (Oracle)',
        filterText: 'drop synonym',
        insertText: 'DROP SYNONYM ${1:synonym_name};',
      },
      {
        id: 'drop-type',
        label: 'DROP TYPE …',
        detail: 'Built-in snippet',
        documentation: 'Drop type (Oracle)',
        filterText: 'drop type force',
        insertText: 'DROP TYPE ${1:schema}.${2:type_name} FORCE;',
      },
    ] as const;
  }

  if (engineId === 'mysql') {
    return [
      {
        id: 'alter-table-add-column',
        label: 'ALTER TABLE … ADD COLUMN …',
        detail: 'Built-in snippet',
        documentation: 'Add a column to a table (MySQL)',
        filterText: 'alter table add column',
        insertText: 'ALTER TABLE ${1:table}\nADD COLUMN ${2:column} ${3:VARCHAR(255)};',
      },
      {
        id: 'alter-table-drop-column',
        label: 'ALTER TABLE … DROP COLUMN …',
        detail: 'Built-in snippet',
        documentation: 'Drop a column from a table',
        filterText: 'alter table drop column',
        insertText: 'ALTER TABLE ${1:table}\nDROP COLUMN ${2:column};',
      },
      {
        id: 'alter-table-modify-column',
        label: 'ALTER TABLE … MODIFY COLUMN …',
        detail: 'Built-in snippet',
        documentation: 'Modify a column type (MySQL)',
        filterText: 'alter table modify column type',
        insertText: 'ALTER TABLE ${1:table}\nMODIFY COLUMN ${2:column} ${3:VARCHAR(255)} NOT NULL;',
      },
      {
        id: 'alter-table-add-foreign-key',
        label: 'ALTER TABLE … ADD CONSTRAINT … FOREIGN KEY …',
        detail: 'Built-in snippet',
        documentation: 'Add a foreign key constraint',
        filterText: 'alter table add constraint foreign key references',
        insertText:
          'ALTER TABLE ${1:table}\nADD CONSTRAINT ${2:fk_name}\nFOREIGN KEY (${3:column})\nREFERENCES ${4:ref_table}(${5:ref_column});',
      },
      {
        id: 'alter-table-add-unique',
        label: 'ALTER TABLE … ADD CONSTRAINT … UNIQUE …',
        detail: 'Built-in snippet',
        documentation: 'Add a unique constraint',
        filterText: 'alter table add constraint unique',
        insertText: 'ALTER TABLE ${1:table}\nADD CONSTRAINT ${2:uq_name}\nUNIQUE (${3:column});',
      },
      {
        id: 'drop-table-if-exists',
        label: 'DROP TABLE IF EXISTS …',
        detail: 'Built-in snippet',
        documentation: 'Drop table if it exists',
        filterText: 'drop table if exists',
        insertText: 'DROP TABLE IF EXISTS ${1:table};',
      },
      {
        id: 'drop-view-if-exists',
        label: 'DROP VIEW IF EXISTS …',
        detail: 'Built-in snippet',
        documentation: 'Drop view if it exists',
        filterText: 'drop view if exists',
        insertText: 'DROP VIEW IF EXISTS ${1:view_name};',
      },
      {
        id: 'drop-index',
        label: 'DROP INDEX … ON …',
        detail: 'Built-in snippet',
        documentation: 'Drop index on a table (MySQL)',
        filterText: 'drop index on',
        insertText: 'DROP INDEX ${1:index_name} ON ${2:table};',
      },
      {
        id: 'drop-procedure-if-exists',
        label: 'DROP PROCEDURE IF EXISTS …',
        detail: 'Built-in snippet',
        documentation: 'Drop procedure if it exists (MySQL)',
        filterText: 'drop procedure if exists',
        insertText: 'DROP PROCEDURE IF EXISTS ${1:proc_name};',
      },
    ] as const;
  }

  if (engineId === 'sqlite') {
    return [
      {
        id: 'alter-table-add-column',
        label: 'ALTER TABLE … ADD COLUMN …',
        detail: 'Built-in snippet',
        documentation: 'Add a column to a table',
        filterText: 'alter table add column',
        insertText: 'ALTER TABLE ${1:table}\nADD COLUMN ${2:column} ${3:TEXT};',
      },
      {
        id: 'alter-table-drop-column',
        label: 'ALTER TABLE … DROP COLUMN …',
        detail: 'Built-in snippet',
        documentation: 'Drop a column from a table',
        filterText: 'alter table drop column',
        insertText: 'ALTER TABLE ${1:table}\nDROP COLUMN ${2:column};',
      },
      {
        id: 'drop-table-if-exists',
        label: 'DROP TABLE IF EXISTS …',
        detail: 'Built-in snippet',
        documentation: 'Drop table if it exists',
        filterText: 'drop table if exists',
        insertText: 'DROP TABLE IF EXISTS ${1:table};',
      },
      {
        id: 'drop-view-if-exists',
        label: 'DROP VIEW IF EXISTS …',
        detail: 'Built-in snippet',
        documentation: 'Drop view if it exists',
        filterText: 'drop view if exists',
        insertText: 'DROP VIEW IF EXISTS ${1:view_name};',
      },
      {
        id: 'drop-index-if-exists',
        label: 'DROP INDEX IF EXISTS …',
        detail: 'Built-in snippet',
        documentation: 'Drop index if it exists',
        filterText: 'drop index if exists',
        insertText: 'DROP INDEX IF EXISTS ${1:index_name};',
      },
    ] as const;
  }

  return [
    {
      id: 'alter-table-add-column',
      label: 'ALTER TABLE … ADD COLUMN …',
      detail: 'Built-in snippet',
      documentation: 'Add a column to a table',
      filterText: 'alter table add column',
      insertText: byEngine(engineId, {
        mssql: 'ALTER TABLE ${1:dbo}.${2:table}\nADD ${3:column} ${4:NVARCHAR(255)} NULL;',
        pgsql: 'ALTER TABLE ${1:public}.${2:table}\nADD COLUMN ${3:column} ${4:TEXT};',
      }),
    },
    {
      id: 'alter-table-drop-column',
      label: 'ALTER TABLE … DROP COLUMN …',
      detail: 'Built-in snippet',
      documentation: 'Drop a column from a table',
      filterText: 'alter table drop column',
      insertText: 'ALTER TABLE ${1:schema}.${2:table}\nDROP COLUMN ${3:column};',
    },
    {
      id: 'alter-table-alter-column',
      label: 'ALTER TABLE … ALTER COLUMN …',
      detail: 'Built-in snippet',
      documentation: 'Alter a column type / nullability',
      filterText: 'alter table alter column type',
      insertText: byEngine(engineId, {
        mssql: 'ALTER TABLE ${1:dbo}.${2:table}\nALTER COLUMN ${3:column} ${4:NVARCHAR(255)} NOT NULL;',
        pgsql: 'ALTER TABLE ${1:public}.${2:table}\nALTER COLUMN ${3:column} TYPE ${4:TEXT};',
      }),
    },
    {
      id: 'alter-table-add-foreign-key',
      label: 'ALTER TABLE … ADD CONSTRAINT … FOREIGN KEY …',
      detail: 'Built-in snippet',
      documentation: 'Add a foreign key constraint',
      filterText: 'alter table add constraint foreign key references',
      insertText:
        'ALTER TABLE ${1:schema}.${2:table}\nADD CONSTRAINT ${3:fk_name}\nFOREIGN KEY (${4:column})\nREFERENCES ${5:schema}.${6:ref_table}(${7:ref_column});',
    },
    {
      id: 'alter-table-add-unique',
      label: 'ALTER TABLE … ADD CONSTRAINT … UNIQUE …',
      detail: 'Built-in snippet',
      documentation: 'Add a unique constraint',
      filterText: 'alter table add constraint unique',
      insertText:
        'ALTER TABLE ${1:schema}.${2:table}\nADD CONSTRAINT ${3:uq_name}\nUNIQUE (${4:column});',
    },
    {
      id: 'drop-table-if-exists',
      label: 'DROP TABLE IF EXISTS …',
      detail: 'Built-in snippet',
      documentation: 'Drop table if it exists',
      filterText: 'drop table if exists',
      insertText: 'DROP TABLE IF EXISTS ${1:schema}.${2:table};',
    },
    {
      id: 'drop-view-if-exists',
      label: 'DROP VIEW IF EXISTS …',
      detail: 'Built-in snippet',
      documentation: 'Drop view if it exists',
      filterText: 'drop view if exists',
      insertText: 'DROP VIEW IF EXISTS ${1:schema}.${2:view_name};',
    },
    {
      id: 'drop-index-if-exists',
      label: 'DROP INDEX IF EXISTS …',
      detail: 'Built-in snippet',
      documentation: 'Drop index if it exists',
      filterText: 'drop index if exists',
      insertText: byEngine(engineId, {
        mssql: 'DROP INDEX IF EXISTS ${1:index_name} ON ${2:dbo}.${3:table};',
        pgsql: 'DROP INDEX IF EXISTS ${1:public}.${2:index_name};',
      }),
    },
    {
      id: 'drop-routine-if-exists',
      label: byEngine(engineId, { mssql: 'DROP PROCEDURE IF EXISTS …', pgsql: 'DROP FUNCTION IF EXISTS …' }),
      detail: 'Built-in snippet',
      documentation: 'Drop a routine if it exists',
      filterText: byEngine(engineId, { mssql: 'drop procedure if exists', pgsql: 'drop function if exists' }),
      insertText: byEngine(engineId, {
        mssql: 'DROP PROCEDURE IF EXISTS ${1:dbo}.${2:proc_name};',
        pgsql: 'DROP FUNCTION IF EXISTS ${1:public}.${2:function_name}(${3:arg_types});',
      }),
    },
  ] as const;
}
